// Reine Logik fuer den Projektteaser Freiflaechen-PV (FfpvTeaserBoard.jsx).
//
// Der Teaser stellt einem Projektpartner eine Flaeche vor - nicht dem
// Eigentuemer. Deshalb kennt diese Datei bewusst keine Kontakt- oder
// Firmendaten, und der Dateiname kommt nur vom Server oder aus der Deal-ID.
// Auf Wunsch des Nutzers stehen Gemarkung, Flur und Flurstueck (seit 28.09.2026) und die
// Anschrift der Flaeche (seit 30.09.2026) drin - nie die des Vertragspartners.
//
// Warum hier und nicht in der Komponente: damit es ohne Browser testbar ist
// (Jest), und damit eine spaetere Handy-Seite dieselben Regeln benutzt
// (AGENTS.md, "Keine zweite App").

export const TEASER_CONTRACT_KEY = 'ffpv-teaser';

// Die Grenze der PDF-Erzeugung liegt bei 2 MB; der Server nimmt deshalb
// hoechstens 1,2 MB Rohbild an. Bewusst dezimal gerechnet (1.200.000 Byte),
// damit ein Bild nie knapp ueber einer binaer gerechneten Grenze landet.
export const BILD_MAX_BYTES = 1200 * 1000;
export const BILD_MAX_KANTE = 1600;
// Was ueberhaupt geoeffnet wird. Handyfotos haben 3-12 MB; darueber ist es
// kein Foto mehr, sondern ein Versehen - und der Browser braeuchte dafuer
// Hunderte Megabyte Speicher.
export const BILD_EINGABE_MAX_BYTES = 40 * 1000 * 1000;
export const BILD_TYPEN = ['image/jpeg', 'image/png', 'image/webp'];
export const BILD_PLATZHALTER = 'FLAECHEN_BILD';

// A4 bei 96 dpi - so rendert der Browser die Vorlage im iframe.
export const A4_BREITE_PX = 794;
export const A4_HOEHE_PX = 1123;

const JPEG_QUALITAETEN = [0.85, 0.75, 0.65, 0.55];
const SCHRUMPF_FAKTOR = 0.8;
const MIN_KANTE = 400;

const PLACEHOLDER_PATTERN = /\{\{\s*([^}]+?)\s*\}\}/g;

// Beschriftung, Gruppe und Herkunft je Platzhalter - aus dem gemeinsamen
// Auftrag (Tabelle "Platzhalter und Standard-Quellen"). Die Reihenfolge hier
// ist die Reihenfolge in der Liste "Angaben".
export const PLATZHALTER_INFO = {
  GEMEINDE: { beschriftung: 'Gemeinde', gruppe: 'Standort', desc: 'Ort aus „PLZ; Ort - Fläche“ (UF_CRM_1752313081525), sonst Gemarkung.' },
  LANDKREIS: { beschriftung: 'Landkreis', gruppe: 'Standort', desc: 'Landkreis (UF_CRM_UF_CRM_LANDKR).' },
  BUNDESLAND: { beschriftung: 'Bundesland', gruppe: 'Standort', desc: 'Bundesland (UF_CRM_1729875617742), Schreibweise vereinheitlicht.' },
  REGION: { beschriftung: 'Region', gruppe: 'Standort', desc: 'Ort · Landkreis · Bundesland, nur vorhandene Teile; ohne alles „Deutschland“.' },
  STRASSE_HNR: { beschriftung: 'Straße, Hausnummer', gruppe: 'Standort', desc: 'Straße, Hausnummer - Fläche (UF_CRM_1752318833571), sonst aus der Projektadresse. „-“ blendet sie aus.' },
  PLZ_ORT: { beschriftung: 'PLZ, Ort', gruppe: 'Standort', desc: 'PLZ; Ort - Fläche (UF_CRM_1752313081525); ohne PLZ aus der Projektadresse, wenn sie eine hat.' },
  GEMARKUNG: { beschriftung: 'Gemarkung', gruppe: 'Standort', desc: 'Gemarkung (UF_CRM_1752323992); Verweise wie „siehe Bild“ gelten als leer. „-“ blendet sie aus.' },
  FLUR: { beschriftung: 'Flur', gruppe: 'Standort', desc: 'Flur (UF_CRM_1752321437), z. B. „9, 10“. „-“ blendet sie aus (Bundesland ohne Flure).' },
  FLURSTUECK: { beschriftung: 'Flurstück-Nr.', gruppe: 'Standort', desc: 'Flurstücke (UF_CRM_1752324013), z. B. „12/15, 15/1“. „-“ blendet sie aus.' },
  FLAECHE_HA: { beschriftung: 'Fläche', gruppe: 'Fläche', desc: 'Fläche in ha (UF_CRM_1752312683120), z. B. „26,6 ha“; ohne Wert „auf Anfrage“.' },
  LEISTUNG_MWP: { beschriftung: 'Leistung', gruppe: 'Fläche', desc: 'Berechnet: Fläche × 1,0 MWp je ha, z. B. „ca. 26,6 MWp“.' },
  NUTZUNG: { beschriftung: 'Nutzung', gruppe: 'Fläche', desc: 'Art der Fläche (UF_CRM_1729877671219).' },
  PACHT: { beschriftung: 'Pacht', gruppe: 'Konditionen', desc: 'PV-Pacht jährlich (UF_CRM_1750205748) oder einmalig (UF_CRM_1750206982); leer → Zeile entfällt.' },
  LAUFZEIT_JAHRE: { beschriftung: 'Laufzeit', gruppe: 'Konditionen', desc: 'Standard 30 Jahre. Das Bitrix-Feld (UF_CRM_1749412087526) steht fast überall auf 20 und gilt nur per Mapping.' },
  NETZBETREIBER: { beschriftung: 'Netzbetreiber', gruppe: 'Netz & Status', desc: 'Netzbetreiber (UF_CRM_1761004313945).' },
  ENTFERNUNG_UW: { beschriftung: 'Entfernung Umspannwerk', gruppe: 'Netz & Status', desc: 'Entfernung in m (UF_CRM_1729878328984), ab 1 km in km.' },
  NETZSTATUS: { beschriftung: 'Netzstatus', gruppe: 'Netz & Status', desc: 'Netzzusage liegt vor → reserviert bis … → Anfrage gestellt am ….' },
  PROJEKTSTATUS: { beschriftung: 'Projektstatus', gruppe: 'Netz & Status', desc: 'In Planung / Ready to Build / In Betrieb (UF_CRM_1768815593603).' },
  // Immer ein fester ENPOWER-Kontakt, nie der Deal-Verantwortliche - der kann
  // ein externer Vertriebspartner sein (Entscheidung des Nutzers, 27.09.2026).
  ANSPRECHPARTNER_NAME: { beschriftung: 'Ansprechpartner', gruppe: 'Ansprechpartner', desc: 'Fester ENPOWER-Kontakt – im Mapping mit raw:Vorname Nachname hinterlegen.' },
  ANSPRECHPARTNER_TEL: { beschriftung: 'Telefon', gruppe: 'Ansprechpartner', desc: 'Fester ENPOWER-Kontakt – im Mapping mit raw:+49 … hinterlegen.' },
  ANSPRECHPARTNER_EMAIL: { beschriftung: 'E-Mail', gruppe: 'Ansprechpartner', desc: 'Fester ENPOWER-Kontakt – im Mapping mit raw:name@en-power.eu hinterlegen.' },
  PROJEKT_NR: { beschriftung: 'Projektnummer', gruppe: 'Dokument', desc: 'Deal-ID als D_<id>.' },
  DATUM: { beschriftung: 'Datum', gruppe: 'Dokument', desc: 'Heutiges Datum (TT.MM.JJJJ).' },
  FLAECHEN_BILD: { beschriftung: 'Bild der Fläche', gruppe: 'Bild', desc: 'Luftbild (UF_CRM_1740525850222), sonst erstes Bild aus „Bilder Fläche“ (UF_CRM_1729855527795); im Tool per Upload ersetzbar.' },
};

const PLATZHALTER_REIHENFOLGE = Object.keys(PLATZHALTER_INFO);
const WEITERE_GRUPPE = 'Weitere';

// Vorschlaege fuer das Mapping. Bewusst ohne CONTACT.* und COMPANY.*: Der
// Teaser geht an einen Partner, Eigentuemerdaten gehoeren nicht hinein.
export const MAPPING_HINWEISE = [
  { value: 'DEAL.ID', label: 'Deal-ID' },
  { value: 'DEAL.TITLE', label: 'Deal-Titel' },
  { value: 'raw:Vorname Nachname', label: 'Fester Ansprechpartner — Name' },
  { value: 'raw:+49 …', label: 'Fester Ansprechpartner — Telefon' },
  { value: 'raw:name@en-power.eu', label: 'Fester Ansprechpartner — E-Mail' },
  { value: 'UF_CRM_1752313081525', label: 'PLZ; Ort - Fläche' },
  { value: 'UF_CRM_UF_CRM_LANDKR', label: 'Landkreis' },
  { value: 'UF_CRM_1729875617742', label: 'Bundesland' },
  { value: 'UF_CRM_1752318833571', label: 'Straße, Hausnummer - Fläche' },
  { value: 'UF_CRM_1750851805', label: 'Projektadresse (Freitext)' },
  { value: 'UF_CRM_1752323992', label: 'Gemarkung' },
  { value: 'UF_CRM_1752321437', label: 'Flur' },
  { value: 'UF_CRM_1752324013', label: 'Flurstück' },
  { value: 'UF_CRM_1752312683120', label: 'Fläche (ha)' },
  { value: 'calc(UF_CRM_1752312683120 * 1)', label: 'Berechnung, z. B. Fläche × 1' },
  { value: 'UF_CRM_1729877671219', label: 'Nutzung' },
  { value: 'UF_CRM_1749412087526', label: 'Laufzeit in Jahren (steht oft auf 20)' },
  { value: 'UF_CRM_1750205748', label: 'PV-Pacht jährlich' },
  { value: 'UF_CRM_1750206982', label: 'PV-Pacht einmalig' },
  { value: 'UF_CRM_1761004313945', label: 'Netzbetreiber' },
  { value: 'UF_CRM_1729878328984', label: 'Entfernung Umspannwerk (m)' },
  { value: 'UF_CRM_1768815593603', label: 'Projektstatus' },
  { value: 'raw:<b>Text</b>', label: 'raw: Text 1:1 (ohne HTML-Maskierung)' },
];

function text(wert) {
  return wert === null || wert === undefined ? '' : String(wert);
}

/**
 * Deal-ID aus dem, was jemand eintippt oder einfuegt: "1234", "D_1234",
 * "D1234", "#1234" oder ein kopierter Bitrix-Link. Leer, wenn nichts passt.
 */
export function dealIdAusEingabe(eingabe) {
  const roh = text(eingabe).trim();
  const ausLink = /\/crm\/deal\/details\/(\d+)/i.exec(roh);
  const treffer = ausLink || /^(?:#|D[_\-\s]?)?(\d{1,10})$/i.exec(roh);
  if (!treffer) return '';
  const ziffern = treffer[1].replace(/^0+/, '');
  return ziffern;
}

export function dealKennung(dealId) {
  const id = text(dealId).trim();
  return id ? `D_${id}` : '';
}

export function beschriftung(platzhalter) {
  return PLATZHALTER_INFO[platzhalter]?.beschriftung || platzhalter;
}

/** "26,6 ha" aus 26.64, "26.64" oder "1,9"; fertiger Text bleibt stehen. */
export function flaecheText(wert) {
  const roh = text(wert).trim();
  if (!roh) return '';
  if (!/^\d+(?:[.,]\d+)?$/.test(roh)) return roh;
  const zahl = Number(roh.replace(',', '.'));
  if (!Number.isFinite(zahl) || zahl <= 0) return '';
  return `${zahl.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ha`;
}

/** Die {{Platzhalter}} einer Vorlage, jeder einmal, in Reihenfolge. */
export function platzhalterAusHtml(html) {
  if (typeof html !== 'string') return [];
  const gesehen = new Set();
  const liste = [];
  PLACEHOLDER_PATTERN.lastIndex = 0;
  let treffer = PLACEHOLDER_PATTERN.exec(html);
  while (treffer !== null) {
    const schluessel = text(treffer[1]).trim();
    if (schluessel && !gesehen.has(schluessel)) {
      gesehen.add(schluessel);
      liste.push(schluessel);
    }
    treffer = PLACEHOLDER_PATTERN.exec(html);
  }
  return liste;
}

/** Gefuellte Zuordnungen, deren Platzhalter nicht (mehr) in der Vorlage steht. */
export function zusatzSchluessel(mapping, platzhalter) {
  const inVorlage = new Set(platzhalter || []);
  return Object.keys(mapping || {})
    .filter((schluessel) => schluessel && text(mapping[schluessel]).trim() && !inVorlage.has(schluessel))
    .sort((a, b) => a.localeCompare(b, 'de'));
}

const HERKUNFT = {
  nachtrag: { text: 'nachgetragen', ton: 'info' },
  mapping: { text: 'Mapping', ton: 'neutral' },
  system: { text: 'Bitrix', ton: 'gut' },
};

/**
 * Wie die Herkunft eines Werts angezeigt wird. "system" heisst fast immer
 * Bitrix - ausser beim Datum, das der Server selbst setzt.
 */
export function herkunft(quelle, { optional = false, platzhalter = '' } = {}) {
  if (quelle === 'leer') {
    return optional ? { text: 'entfällt', ton: 'neutral' } : { text: 'fehlt', ton: 'warn' };
  }
  if (quelle === 'system' && platzhalter === 'DATUM') return { text: 'automatisch', ton: 'neutral' };
  return HERKUNFT[quelle] || { text: text(quelle) || 'unbekannt', ton: 'neutral' };
}

function gruppenRang(gruppe) {
  const gruppen = [...new Set(PLATZHALTER_REIHENFOLGE.map((p) => PLATZHALTER_INFO[p].gruppe)), WEITERE_GRUPPE];
  return gruppen.indexOf(gruppe);
}

function rang(platzhalter) {
  const i = PLATZHALTER_REIHENFOLGE.indexOf(platzhalter);
  return i === -1 ? PLATZHALTER_REIHENFOLGE.length : i;
}

/**
 * Die Felder aus der Vorschau fuer die Liste "Angaben": ohne das Bild (das
 * hat einen eigenen Baustein), nach Gruppen geordnet, mit Nachtrag und
 * Herkunft. Nachtraege kommen aus dem lokalen Stand, damit die Liste nicht
 * erst nach der naechsten Vorschau zeigt, was man eingetragen hat.
 */
export function felderAufbereiten(felder, overrides) {
  const nachtraege = overrides || {};
  const zeilen = (Array.isArray(felder) ? felder : [])
    .filter((feld) => feld && feld.platzhalter && feld.platzhalter !== BILD_PLATZHALTER)
    .map((feld) => {
      const info = PLATZHALTER_INFO[feld.platzhalter];
      const nachtrag = text(nachtraege[feld.platzhalter]);
      const wert = text(feld.wert);
      return {
        platzhalter: feld.platzhalter,
        beschriftung: info?.beschriftung || feld.platzhalter,
        gruppe: info?.gruppe || WEITERE_GRUPPE,
        beschreibung: text(feld.beschreibung) || info?.desc || '',
        wert,
        quelle: feld.quelle,
        optional: feld.optional === true,
        nachtrag,
        nachgetragen: Boolean(nachtrag.trim()),
        leer: !wert.trim() && !nachtrag.trim(),
      };
    })
    .sort((a, b) => (gruppenRang(a.gruppe) - gruppenRang(b.gruppe)) || (rang(a.platzhalter) - rang(b.platzhalter)));

  return {
    zeilen,
    fehlendPflicht: zeilen.filter((z) => z.leer && !z.optional).length,
    entfallen: zeilen.filter((z) => z.leer && z.optional).length,
  };
}

/**
 * Nachtraege fuer den Server: getrimmt, leere weg. Ein leerer Nachtrag heisst
 * "keiner" - der Wert aus Bitrix bzw. dem Mapping gilt dann wieder.
 */
export function nachtraegeBereinigen(overrides) {
  return Object.entries(overrides || {}).reduce((ergebnis, [schluessel, wert]) => {
    if (!schluessel || wert === null || wert === undefined || typeof wert === 'object') return ergebnis;
    const sauber = String(wert).trim();
    return sauber ? { ...ergebnis, [schluessel]: sauber } : ergebnis;
  }, {});
}

/**
 * Fingerabdruck des Stands, aus dem eine Vorschau entstand. Weicht der
 * aktuelle davon ab, ist die Vorschau veraltet.
 */
export function vorschauSchluessel({ dealId, overrides, bild }) {
  const sauber = nachtraegeBereinigen(overrides);
  const geordnet = Object.keys(sauber).sort().map((k) => [k, sauber[k]]);
  const bildTeil = bild ? `${bild.bytes || 0}:${text(bild.base64).length}:${text(bild.base64).slice(-24)}` : '';
  return JSON.stringify([text(dealId), geordnet, bildTeil]);
}

/**
 * Die Bildquelle fuer die Anzeige: aus bild.src der Vorschau (Data-URI) oder
 * aus einem <img> im Wert von FLAECHEN_BILD. Nur Data-URIs und https - alles
 * andere (Platzhalterkasten, Beschreibungstext, fremde Schemata) ergibt ''.
 */
export function bildAusFeld(wert) {
  const roh = text(wert).trim();
  if (!roh) return '';
  const quelle = /^(data:image\/|https:\/\/)/i.test(roh)
    ? roh
    : (/<img\b[^>]*\bsrc\s*=\s*(["'])(.*?)\1/i.exec(roh) || [])[2] || '';
  return /^(data:image\/[a-z0-9.+-]+;base64,|https:\/\/)/i.test(quelle) ? quelle : '';
}

/** Vorab-Pruefung der gewaehlten Datei, bevor der Browser sie oeffnet. */
export function pruefeBildDatei(datei) {
  if (!datei) return { ok: false, fehler: 'Keine Datei gewählt.' };
  if (!/^image\//i.test(text(datei.type))) return { ok: false, fehler: 'Das ist kein Bild. Bitte JPEG, PNG oder WebP wählen.' };
  if (!datei.size) return { ok: false, fehler: 'Die Datei ist leer.' };
  if (datei.size > BILD_EINGABE_MAX_BYTES) return { ok: false, fehler: 'Die Datei ist zu groß (über 40 MB).' };
  return { ok: true, fehler: '' };
}

/** Laengste Kante auf hoechstens maxKante, Seitenverhaeltnis bleibt, nie groesser. */
export function zielMasse(breite, hoehe, maxKante = BILD_MAX_KANTE) {
  const faktor = Math.min(1, maxKante / Math.max(breite, hoehe, 1));
  return { breite: Math.max(1, Math.round(breite * faktor)), hoehe: Math.max(1, Math.round(hoehe * faktor)) };
}

/** Darf die Datei so, wie sie ist, zum Server? Sonst wird sie neu kodiert. */
export function brauchtVerkleinerung({ typ, bytes, breite, hoehe }, { maxKante = BILD_MAX_KANTE, maxBytes = BILD_MAX_BYTES } = {}) {
  return !BILD_TYPEN.includes(text(typ).toLowerCase())
    || bytes > maxBytes
    || Math.max(breite, hoehe) > maxKante;
}

/**
 * Sucht die beste JPEG-Fassung unter maxBytes: erst die Qualitaet senken,
 * dann die Masse. kodiere(breite, hoehe, qualitaet) liefert einen Blob (im
 * Browser canvas.toBlob) - uebergeben, damit das hier ohne Canvas testbar ist.
 */
export async function passendKodieren({
  breite, hoehe, kodiere, maxKante = BILD_MAX_KANTE, maxBytes = BILD_MAX_BYTES,
}) {
  let masse = zielMasse(breite, hoehe, maxKante);
  while (Math.max(masse.breite, masse.hoehe) >= Math.min(MIN_KANTE, Math.max(breite, hoehe))) {
    for (const qualitaet of JPEG_QUALITAETEN) {
      // Nacheinander, nicht parallel: jede Kodierung belegt ein volles Bild im Speicher.
      const blob = await kodiere(masse.breite, masse.hoehe, qualitaet);
      if (!blob) throw new Error('Das Bild ließ sich nicht umwandeln.');
      if (blob.size <= maxBytes) return { blob, ...masse, qualitaet };
    }
    const kleiner = { breite: Math.round(masse.breite * SCHRUMPF_FAKTOR), hoehe: Math.round(masse.hoehe * SCHRUMPF_FAKTOR) };
    if (Math.max(kleiner.breite, kleiner.hoehe) < MIN_KANTE) break;
    masse = kleiner;
  }
  throw new Error('Das Bild lässt sich nicht unter 1,2 MB bringen. Bitte einen Ausschnitt oder ein anderes Bild wählen.');
}

/** "data:image/webp;base64,XXX" → { base64: 'XXX', mimeType: 'image/webp' } */
export function bildNutzlast(dataUrl) {
  const treffer = /^data:([^;,]+);base64,(.+)$/i.exec(text(dataUrl));
  return treffer ? { base64: treffer[2], mimeType: treffer[1].toLowerCase() } : null;
}

// --- Browser-Teil der Bildvorbereitung. Wird im Test durch Fakes ersetzt. ---

function ladeBildImBrowser(datei) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(datei);
    const bild = new Image();
    bild.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ breite: bild.naturalWidth, hoehe: bild.naturalHeight, quelle: bild });
    };
    bild.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Bild nicht lesbar'));
    };
    bild.src = url;
  });
}

function kodiereImBrowser(quelle, breite, hoehe, qualitaet) {
  const leinwand = document.createElement('canvas');
  leinwand.width = breite;
  leinwand.height = hoehe;
  const kontext = leinwand.getContext('2d');
  // Weiss unterlegen: JPEG kennt keine Transparenz, ein PNG-Ausschnitt
  // bekaeme sonst schwarze Flaechen.
  kontext.fillStyle = '#FFFFFF';
  kontext.fillRect(0, 0, breite, hoehe);
  kontext.imageSmoothingQuality = 'high';
  kontext.drawImage(quelle, 0, 0, breite, hoehe);
  return new Promise((resolve) => leinwand.toBlob(resolve, 'image/jpeg', qualitaet));
}

function alsDataUrlImBrowser(blob) {
  return new Promise((resolve, reject) => {
    const leser = new FileReader();
    leser.onload = () => resolve(String(leser.result || ''));
    leser.onerror = () => reject(new Error('Bild nicht lesbar'));
    leser.readAsDataURL(blob);
  });
}

const BROWSER_HELFER = { lade: ladeBildImBrowser, kodiere: kodiereImBrowser, alsDataUrl: alsDataUrlImBrowser };

/**
 * Macht aus einer gewaehlten Datei (Datei oder Kamera) das Bild fuer den
 * Server: hoechstens 1600 px und 1,2 MB. Passt die Datei schon, geht sie
 * unveraendert - ein Luftbild als PNG bleibt dann scharf.
 * Ergebnis: { base64, mimeType, dataUrl, bytes, breite, hoehe, name, verkleinert }
 */
export async function bildVorbereiten(datei, helfer = BROWSER_HELFER) {
  const pruefung = pruefeBildDatei(datei);
  if (!pruefung.ok) throw new Error(pruefung.fehler);

  let geladen;
  try {
    geladen = await helfer.lade(datei);
  } catch {
    throw new Error('Das Bild lässt sich nicht öffnen. Bitte als JPEG oder PNG speichern und erneut wählen.');
  }

  const { breite, hoehe, quelle } = geladen;
  const name = text(datei.name);
  if (!brauchtVerkleinerung({ typ: datei.type, bytes: datei.size, breite, hoehe })) {
    const dataUrl = await helfer.alsDataUrl(datei);
    const nutzlast = bildNutzlast(dataUrl);
    if (!nutzlast) throw new Error('Das Bild ließ sich nicht lesen.');
    return { ...nutzlast, mimeType: text(datei.type).toLowerCase(), dataUrl, bytes: datei.size, breite, hoehe, name, verkleinert: false };
  }

  const kodiert = await passendKodieren({
    breite, hoehe, kodiere: (b, h, q) => helfer.kodiere(quelle, b, h, q),
  });
  const dataUrl = await helfer.alsDataUrl(kodiert.blob);
  const nutzlast = bildNutzlast(dataUrl);
  if (!nutzlast) throw new Error('Das Bild ließ sich nicht umwandeln.');
  return {
    ...nutzlast,
    mimeType: 'image/jpeg',
    dataUrl,
    bytes: kodiert.blob.size,
    breite: kodiert.breite,
    hoehe: kodiert.hoehe,
    name,
    verkleinert: true,
  };
}

function dateinameSaeubern(name) {
  const ohnePfad = text(name).split(/[\\/]/).pop() || '';
  // eslint-disable-next-line no-control-regex
  return ohnePfad.replace(/[\u0000-\u001f"<>|:*?]/g, '').trim();
}

/** Dateiname aus content-disposition; filename* (UTF-8) vor filename. */
export function dateinameAusDisposition(disposition, ersatz) {
  const kopf = text(disposition);
  const utf8 = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(kopf);
  if (utf8) {
    try {
      const name = dateinameSaeubern(decodeURIComponent(utf8[1].trim()));
      if (name) return name;
    } catch {
      // kaputte Prozent-Kodierung: weiter mit filename=
    }
  }
  const einfach = /filename\s*=\s*(?:"([^"]*)"|([^;]+))/i.exec(kopf.replace(/filename\*\s*=[^;]*;?/gi, ''));
  const name = dateinameSaeubern(einfach ? (einfach[1] ?? einfach[2]) : '');
  return name || ersatz;
}

/** Nur die Deal-ID - nie ein Eigentuemername. */
export function ersatzDateiname(dealId) {
  return `Projektteaser-FFPV_D${text(dealId).trim()}.pdf`;
}

const TON_RANG = { gut: 0, warn: 1, gefahr: 2 };

function schlechter(a, b) {
  return TON_RANG[b] > TON_RANG[a] ? b : a;
}

/**
 * Was nach "Erzeugen" gemeldet wird, aus x-teaser-bitrix und x-teaser-phase.
 * Fehlt ein Header, wird nichts behauptet, sondern zum Nachsehen geraten.
 */
export function erzeugenErgebnis({ bitrix, phase, anBitrix, phaseWechseln }) {
  if (!anBitrix) return { ton: 'gut', zeilen: ['PDF erstellt.'] };

  const b = text(bitrix).trim().toLowerCase();
  const p = text(phase).trim().toLowerCase();
  const zeilen = [];
  let ton = 'gut';

  if (b === 'kommentiert') {
    zeilen.push('PDF erstellt und im Deal abgelegt (Kommentar in der Timeline).');
  } else if (b === 'fehler') {
    zeilen.push('PDF erstellt, aber nicht in den Deal gelegt – Bitrix hat abgelehnt oder ist nicht erreichbar.');
    ton = 'gefahr';
  } else if (b === 'nein') {
    zeilen.push('PDF erstellt, aber nicht in den Deal gelegt.');
    ton = schlechter(ton, 'warn');
  } else {
    zeilen.push('PDF erstellt. Ob es im Deal liegt, hat der Server nicht gemeldet – bitte in Bitrix nachsehen.');
    ton = schlechter(ton, 'warn');
  }

  if (phaseWechseln) {
    if (p === 'gewechselt') {
      zeilen.push('Phase auf „Teaser fertig“ gesetzt.');
    } else if (p === 'fehler') {
      zeilen.push('Die Phase ließ sich nicht wechseln.');
      ton = schlechter(ton, 'gefahr');
    } else if (p === 'nein') {
      zeilen.push('Die Phase wurde nicht gewechselt – gibt es „Teaser fertig“ in Bitrix schon?');
      ton = schlechter(ton, 'warn');
    } else {
      zeilen.push('Ob die Phase gewechselt wurde, hat der Server nicht gemeldet – bitte in Bitrix nachsehen.');
      ton = schlechter(ton, 'warn');
    }
  }

  return { ton, zeilen };
}

const STATUS_MELDUNG = {
  400: 'Die Anfrage war unvollständig.',
  401: 'Die Anmeldung ist abgelaufen. Bitte neu einloggen.',
  403: 'Dafür fehlt die Berechtigung – oder der Deal ist für dich nicht freigegeben.',
  404: 'Deal nicht gefunden.',
  413: 'Das Bild ist zu groß (höchstens 1,2 MB).',
  502: 'Bitrix oder die PDF-Erzeugung antwortet gerade nicht. Bitte gleich noch einmal versuchen.',
  503: 'Der Dienst ist gerade nicht erreichbar. Bitte gleich noch einmal versuchen.',
  504: 'Das hat zu lange gedauert. Bitte noch einmal versuchen.',
};

/** Verstaendlicher Satz zu einem Fehlerstatus; ein Detail vom Server gewinnt. */
export function fehlerMeldung(status, detail) {
  const vomServer = text(detail).trim();
  if (vomServer) return vomServer;
  if (!status) return 'Keine Verbindung zum Server.';
  return STATUS_MELDUNG[status] || `Fehler (${status}).`;
}

/** "Fehlt: Gemeinde, Pacht" fuer die Warteschlange. */
export function fehlendText(fehlend) {
  const liste = Array.isArray(fehlend) ? fehlend.filter(Boolean) : [];
  return liste.length ? `Fehlt: ${liste.map(beschriftung).join(', ')}` : '';
}

export function bytesText(bytes) {
  if (!bytes) return '';
  if (bytes < 1000 * 1000) return `${Math.round(bytes / 1000)} KB`;
  return `${(bytes / (1000 * 1000)).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`;
}

/** Massstab, mit dem die A4-Seite in die verfuegbare Breite passt; nie > 1. */
export function vorschauMassstab(verfuegbar, seitenBreite = A4_BREITE_PX) {
  if (!verfuegbar || verfuegbar <= 0) return 0.6;
  return Math.min(1, verfuegbar / seitenBreite);
}
